<template>
  <div>
    <b-breadcrumb class="breadcrumb-pipes">
      <b-breadcrumb-item href="/settings">
        Admin Panel
      </b-breadcrumb-item>
      <b-breadcrumb-item active>
        SFC Prop Accounts
      </b-breadcrumb-item>
    </b-breadcrumb>

    <b-overlay :show="loading" rounded="sm" opacity="0.6" no-fade>
      <b-card class="mt-2">
        <template #header>
          <div class="d-flex justify-content-between align-items-center" style="width:100%">
            <h4 class="mb-0">SFC Prop Accounts</h4>
            <b-button
              v-ripple.400="'rgba(113, 102, 240, 0.15)'"
              variant="primary"
              size="sm"
              :disabled="saving"
              @click="saveDetails"
            >
              <b-spinner v-if="saving" small class="mr-50" />
              <feather-icon v-else icon="SaveIcon" class="mr-50" />
              <span class="align-middle">Save</span>
            </b-button>
          </div>
        </template>

        <b-card-body>
          <p class="text-muted" style="font-size:0.85rem">
            SFC's own account ID Schema at each venue, per client settlement
            venue. Used by the Intesa SetWeb report (COD_CTP).
          </p>

          <b-table
            striped
            hover
            class="position-relative"
            :items="details"
            :fields="tableColumns"
            show-empty
            empty-text="No venues configured yet. Add venues in Venue Settings first."
          >
            <template #cell(IdSchema)="data">
              <b-form-input size="sm" type="text" v-model="data.item.IdSchema" />
            </template>
          </b-table>
        </b-card-body>
      </b-card>
    </b-overlay>
  </div>
</template>

<script>
import {
  BCard, BCardBody, BTable, BButton, BFormInput,
  BOverlay, BSpinner, BBreadcrumb, BBreadcrumbItem,
} from "bootstrap-vue";
import store from "@/store";
import { onUnmounted } from "@vue/composition-api";
import settingsStoreModule from "../../settingsStoreModule";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import Ripple from "vue-ripple-directive";

const SETTINGS_APP_STORE_MODULE_NAME = "settings";

export default {
  components: {
    BCard, BCardBody, BTable, BButton, BFormInput,
    BOverlay, BSpinner, BBreadcrumb, BBreadcrumbItem, ToastificationContent,
  },

  directives: {
    Ripple,
  },

  setup() {
    if (!store.hasModule(SETTINGS_APP_STORE_MODULE_NAME)) {
      store.registerModule(SETTINGS_APP_STORE_MODULE_NAME, settingsStoreModule);
    }
    onUnmounted(() => {
      if (store.hasModule(SETTINGS_APP_STORE_MODULE_NAME)) {
        store.unregisterModule(SETTINGS_APP_STORE_MODULE_NAME);
      }
    });
  },

  data() {
    return {
      loading: false,
      saving: false,
      optVenues: [],
      details: [],
      tableColumns: [
        { key: "PlaceOfSettlement", label: "SFC Settlement Venue" },
        { key: "CounterpartyPlace", label: "Client Settlement Venue" },
        { key: "IdSchema", label: "Account ID Schema (COD_CTP)" },
      ],
    };
  },

  mounted() {
    this.getVenues();
  },

  methods: {
    defaultDetails() {
      const abbreviations = (this.optVenues || []).map((v) => v.Abbreviation);
      const details = [];
      abbreviations.forEach((place) => {
        abbreviations.forEach((counterparty) => {
          details.push({
            PlaceOfSettlement: place,
            CounterpartyPlace: counterparty,
            IdSchema: null,
          });
        });
      });
      return details;
    },

    getVenues() {
      this.loading = true;
      store
        .dispatch("settings/getVenues")
        .then((res) => {
          this.optVenues = res.data || [];
          this.getDetails();
        })
        .catch((e) => {
          this.loading = false;
          this.showError(`Error fetching venues: ${e.message}`);
        });
    },

    getDetails() {
      store
        .dispatch("settings/getSfcPropAccounts")
        .then((res) => {
          const rows = res.data.resData || [];
          this.details = this.defaultDetails().map((detail) => {
            const found = rows.find(
              (row) =>
                (row.PlaceOfSettlement || "").toUpperCase() ===
                  detail.PlaceOfSettlement &&
                (row.CounterpartyPlace || "").toUpperCase() ===
                  detail.CounterpartyPlace
            );
            return found
              ? {
                  PlaceOfSettlement: detail.PlaceOfSettlement,
                  CounterpartyPlace: detail.CounterpartyPlace,
                  IdSchema: found.IdSchema,
                }
              : detail;
          });
          this.loading = false;
        })
        .catch((e) => {
          this.loading = false;
          this.showError(`Error fetching SFC prop accounts: ${e.message}`);
        });
    },

    saveDetails() {
      this.saving = true;
      store
        .dispatch("settings/saveSfcPropAccounts", { details: this.details })
        .then((res) => {
          this.saving = false;
          if (res.data.info === "Pass") {
            this.$toast({
              component: ToastificationContent,
              props: {
                title: "Success",
                text: "SFC prop accounts saved",
                icon: "CheckIcon",
                variant: "success",
              },
            });
          } else {
            this.showError(res.data.message);
          }
        })
        .catch((e) => {
          this.saving = false;
          this.showError(`Error saving SFC prop accounts: ${e.message}`);
        });
    },

    showError(msg) {
      this.$toast({
        component: ToastificationContent,
        props: { title: "Error", text: msg, icon: "AlertTriangleIcon", variant: "danger" },
      });
    },
  },
};
</script>
